import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import uuid from 'uuid';

import AddItemButton from 'components/PdfViewer/add-items-controls/AddItemButton';
import { displayFirstPageOnly, loadDocumentBlobFromUrl } from 'components/PdfViewer/utils';
import { ZoomControls } from 'components/PdfViewer/zoom-controls';
import config from 'config/config';
import { getPlan } from 'containers/OneProjectPage/PlansPage/actions';
import { useFeatureFlags } from 'contexts/FeatureFlagsProvider';
import { Routing } from 'routing/routing';
import combineStyles from 'theme/combineStyles';
import { styles as mapTheme } from 'theme/map-interaction-theme';
import { CLASSIC_TYPO } from 'theme/typography';
import Analytics from 'utils/Analytics';
import { getPlanFileFormat } from 'utils/file';
import localStorageUser from 'utils/localStorageUser';
import { ensurePdftronInitialized, reportPdftronException } from 'utils/pdftron.utils';

const styles = theme => ({
  scrollView: {
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'auto',

    '& .pageContainer canvas:last-child': {
      position: 'relative !important',
    },
  },
  scrollViewNoScroll: {
    height: '100%',
    width: '100%',
    bottom: 0,
    display: 'flex',

    '& canvas': {
      left: 0,
      width: '100% !important',
    },
    '& .pageSection': {
      display: 'none',
    },
    '& .pageSection:first-of-type': {
      margin: '0 !important',
      width: '100% !important',
      display: 'inherit',
    },
    '& .pageContainer': {
      margin: '0 !important',
      width: '100% !important',
    },
  },
  viewer: {
    position: 'absolute',
    margin: 'auto',
    top: 0,
    left: 0,
    right: 0,
  },
  viewerNoScroll: {
    cursor: 'inherit !important',
    margin: 0,
    display: 'flex',
    width: '100% !important',
  },
  path: {
    cursor: 'pointer',
    '&:hover': {
      stroke: theme.palette.info.main,
      strokeWidth: 4,
      strokeLinecap: 'square',
    },
  },
  actionSection: {
    position: 'absolute',
    right: theme.spacing(6),
    display: 'grid',
    gridTemplateRows: `repeat(3, 1fr)`,
    justifyContent: 'center',
    alignItems: 'stretch',
    zIndex: 100,
    gap: theme.spacing(2),
    height: '100%',
  },
  zoomContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    justifyContent: 'center',
  },
});

const PdfTronViewer = ({
  changeLoader,
  classes,
  docUrl,
  entityType,
  history,
  match,
  onPageLoadSuccess,
  overflow = true,
  pins = [],
  onPlacePin,
  shouldAddClickableZones,
  tableItems,
  zonesData,
  moduleId,
  addZoomButton = true,
  mouseWheelZoom = false,
  seeAnnotations,
  onPageLoadError,
  isFirstPageOnly,
  updatePin,
  dispatchGetPlan,
  renderPin,
}) => {
  const { params } = match;
  const { organizationId, idData, itemId } = params;
  const [showPins, setShowPins] = useState(true);
  const featureFlags = useFeatureFlags();
  const [tronId] = useState(() => uuid.v4());

  const [documentViewer, setDocumentViewer] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const loadedUrlRef = useRef(null);
  const [zoomValue, setZoomValue] = useState(1);
  const clickableZonesRootRef = useRef(null);
  const zoomScale = zoomValue > 1 ? 0.25 : 0.1;
  const shouldAddItems = featureFlags?.plan_add_items;

  const resetDimensions = () => {
    const viewerEl = document.getElementById(`viewer-${tronId}`);
    if (!viewerEl) return;

    const elPageContainer =
      viewerEl.querySelector('.pageContainer') || document.getElementById('pageContainer1');

    if (elPageContainer) {
      setDimensions({
        width: elPageContainer.offsetWidth,
        height: elPageContainer.offsetHeight,
      });
    } else if (documentViewer) {
      const zoom = documentViewer.getZoomLevel();
      setDimensions({
        width: documentViewer.getPageWidth(1) * zoom,
        height: documentViewer.getPageHeight(1) * zoom,
      });
    }
  };

  const zoomOut = () => {
    if (!documentViewer) return;
    const newZoom = documentViewer.getZoomLevel() - zoomScale;
    setZoomValue(newZoom);
    documentViewer.zoomTo(newZoom);
  };

  const zoomIn = () => {
    if (!documentViewer) return;
    const newZoom = documentViewer.getZoomLevel() + zoomScale;
    setZoomValue(newZoom);
    documentViewer.zoomTo(newZoom);
  };

  const onRedirectToPlan = plan => {
    Analytics.track('plans_plan_opened', {
      source: 'folder_overview',
      file_format: getPlanFileFormat(plan),
    });
    localStorageUser.addRecentlyViewedPlans({
      ...plan,
      folder_id: itemId || 'root',
      project_id: idData,
    });

    history.push(
      Routing.plans.plan(organizationId, idData, plan?.folder_id || itemId, plan.id, 'plan'),
    );
  };

  const handleShapeClick = localisation => () => {
    const clickedItem = tableItems.find(item => item.id === localisation.id);
    if (clickedItem == null) {
      dispatchGetPlan(localisation.id, onRedirectToPlan, null);
    } else {
      onRedirectToPlan(clickedItem);
    }
  };

  const processLocalisationCoordinate = (coord, index, localisation) => {
    let commands = '';
    const xPixels = Math.floor(dimensions.width * coord.x);
    const yPixels = Math.floor(dimensions.height * coord.y);
    if (index === 0) {
      commands += `M ${xPixels} ${yPixels} `;
    } else if (index === localisation.coordinates.length - 1) {
      commands += `L ${xPixels} ${yPixels} `;
      const xFirst = Math.floor(dimensions.width * localisation.coordinates[0].x);
      const yFirst = Math.floor(dimensions.height * localisation.coordinates[0].y);
      commands += `L ${xFirst} ${yFirst} `;
    } else {
      commands += `L ${xPixels} ${yPixels} `;
    }
    return commands;
  };

  const returnText = localisation => {
    if (!localisation.text_coordinates) {
      return null;
    }
    const xPixels = Math.floor(dimensions.width * localisation.text_coordinates.x_min);
    const yPixels = Math.floor(dimensions.height * localisation.text_coordinates.y_max);
    const fSize = Math.round(
      Math.ceil(Math.round(dimensions.width) * localisation.text_coordinates.font_size),
    );
    const plan = tableItems.find(item => item.id === localisation.id);

    return (
      <foreignObject
        key={`clickabletext-${plan?.id}`}
        x={xPixels}
        y={yPixels}
        width={Math.floor(
          dimensions.width * localisation.text_coordinates.x_max -
            dimensions.width * localisation.text_coordinates.x_min,
        )}
        height={Math.floor(
          dimensions.height * localisation.text_coordinates.y_min -
            dimensions.height * localisation.text_coordinates.y_max,
        )}
        onClick={handleShapeClick(localisation)}
        style={{ overflow: 'visible', cursor: 'pointer' }}
      >
        <div style={{ font: `${fSize}px ${CLASSIC_TYPO}`, overflow: 'visible' }}>{plan?.name}</div>
      </foreignObject>
    );
  };

  const drawIslandCoordinates = localisation => {
    let commands = '';
    if (localisation.islands_coordinates) {
      localisation.islands_coordinates.forEach(island => {
        const reversedIsland = island.slice().reverse();
        reversedIsland.forEach((coord, index) => {
          const xPixels = Math.floor(dimensions.width * coord.x);
          const yPixels = Math.floor(dimensions.height * coord.y);

          if (index === 0) {
            commands += `M ${xPixels} ${yPixels} `;
          } else if (index === reversedIsland.length - 1) {
            commands += `L ${xPixels} ${yPixels} `;
            const xFirst = Math.floor(dimensions.width * reversedIsland[0].x);
            const yFirst = Math.floor(dimensions.height * reversedIsland[0].y);
            commands += `L ${xFirst} ${yFirst} `;
          } else {
            commands += `L ${xPixels} ${yPixels} `;
          }
        });
      });
    }
    return commands;
  };

  const getLayerContent = (localisation, includeAreaAndNames) => {
    const rgbaColor = localisation.color;
    const rgbaFromArgb = `#${rgbaColor.slice(3)}${rgbaColor.slice(1, 3)}`;
    const color = !includeAreaAndNames ? `${rgbaFromArgb}` : '#00000000';
    const uniqueKey = `path-${localisation.id}`;
    let pathCommands = '';
    localisation.coordinates.forEach((coord, index) => {
      pathCommands += processLocalisationCoordinate(coord, index, localisation);
    });
    pathCommands += drawIslandCoordinates(localisation);
    return (
      <path
        className={classes.path}
        key={uniqueKey}
        fill={color}
        d={pathCommands}
        onClick={handleShapeClick(localisation)}
      />
    );
  };

  const getClickableZones = () => {
    if (!shouldAddClickableZones) {
      return <div />;
    }
    return (
      <svg width={dimensions.width} height={dimensions.height} fillRule="evenodd">
        {shouldAddClickableZones &&
          zonesData.folder.localisations.map(localisation =>
            getLayerContent(localisation, zonesData.folder.includes_area_and_names),
          )}
        {shouldAddClickableZones &&
          !zonesData.folder.includes_area_and_names &&
          zonesData.folder.localisations.map(localisation => returnText(localisation))}
      </svg>
    );
  };

  const drawSvgClickableZones = () => {
    if (clickableZonesRootRef.current) {
      if (document.querySelector('.clickable-zones')) {
        clickableZonesRootRef.current.render(getClickableZones());
        return;
      }
      clickableZonesRootRef.current = null;
    }

    const node = document.createElement('div');
    node.className = 'clickable-zones';
    node.style.width = '100%';
    node.style.height = '100%';
    node.style.position = 'absolute';
    node.style.zIndex = 1000;
    node.style.top = '0';
    node.style.left = '0';

    const viewerEl = document.getElementById(`viewer-${tronId}`);
    const elPageContainer =
      viewerEl?.querySelector('.pageContainer') || document.getElementById('pageContainer1');

    if (elPageContainer) {
      elPageContainer.appendChild(node);
      clickableZonesRootRef.current = createRoot(node);
      clickableZonesRootRef.current.render(getClickableZones());
    }
  };

  useEffect(() => {
    let mounted = true;
    let viewerInstance = null;
    let scrollViewEl = null;
    let onDocumentLoaded = null;
    let onDocumentLoadFailed = null;
    let onZoomUpdated = null;

    const getMouseLocation = viewerEl => {
      const elementRect = viewerEl.getBoundingClientRect();
      const x = elementRect.left + scrollViewEl.scrollLeft;
      const y = elementRect.top + scrollViewEl.scrollTop;
      return { x, y };
    };

    const handleMouseWheelZoom = e => {
      let throttleTime;
      e.stopPropagation();
      e.preventDefault();

      if (!throttleTime) {
        throttleTime = setTimeout(() => {
          const viewerEl = document.getElementById(`viewer-${tronId}`);
          if (!viewerEl || !viewerInstance) return;
          const mouseLocation = getMouseLocation(viewerEl);
          const scaleFactor = e.deltaMode === 0 ? 0.05 : 0.3;

          if (e.deltaY < 0) {
            viewerInstance.zoomToMouse(
              viewerInstance.getZoomLevel() * (1 + scaleFactor),
              mouseLocation.x,
              mouseLocation.y,
            );
          } else {
            viewerInstance.zoomToMouse(
              viewerInstance.getZoomLevel() * (1 - scaleFactor),
              mouseLocation.x,
              mouseLocation.y,
            );
          }
          clearTimeout(throttleTime);
          throttleTime = null;
        }, 100);
      }
    };

    (async () => {
      try {
        await ensurePdftronInitialized();
        if (!mounted) return;

        scrollViewEl = document.getElementById(`scroll-view-${tronId}`);
        const viewerEl = document.getElementById(`viewer-${tronId}`);
        if (!scrollViewEl || !viewerEl) {
          return;
        }

        viewerInstance = new window.Core.DocumentViewer();
        setDocumentViewer(viewerInstance);

        if (seeAnnotations) viewerInstance.enableAnnotations();
        else viewerInstance.disableAnnotations();

        viewerInstance.setScrollViewElement(scrollViewEl);
        viewerInstance.setViewerElement(viewerEl);

        if (changeLoader) {
          changeLoader(true);
        }

        if (mouseWheelZoom) {
          scrollViewEl.addEventListener('wheel', handleMouseWheelZoom, { passive: false });
        }

        onDocumentLoaded = () => {
          if (isFirstPageOnly) displayFirstPageOnly(viewerInstance);
          const rotation = viewerInstance.getCompleteRotation(1);
          viewerInstance.setMargin(0);

          onPageLoadSuccess(rotation);
          if (!overflow) {
            viewerInstance.setFitMode(viewerInstance.FitMode.FitWidth);
          }
          viewerInstance.setToolMode(viewerInstance.getTool(window.Core.Tools.ToolNames.PAN));

          if (changeLoader) {
            changeLoader(false);
          }
          resetDimensions();
        };

        onDocumentLoadFailed = err => {
          reportPdftronException(err, 'LITE_DOCUMENT_LOAD_EVENT_FAILED', {
            tron_id: tronId,
            doc_url: docUrl,
          });
          if (changeLoader) {
            changeLoader(false);
          }
          if (onPageLoadError) {
            onPageLoadError(err);
          }
        };

        onZoomUpdated = () => resetDimensions();

        viewerInstance.addEventListener('documentLoaded', onDocumentLoaded);
        viewerInstance.addEventListener('documentLoadFailed', onDocumentLoadFailed);
        viewerInstance.addEventListener('zoomUpdated', onZoomUpdated);
      } catch (err) {
        reportPdftronException(err, 'LITE_VIEWER_INIT_FAILED', {
          tron_id: tronId,
          doc_url: docUrl,
        });
        if (changeLoader) {
          changeLoader(false);
        }
        if (onPageLoadError) {
          onPageLoadError(err);
        }
      }
    })();

    return () => {
      mounted = false;
      if (scrollViewEl) {
        scrollViewEl.removeEventListener('wheel', handleMouseWheelZoom);
      }

      if (viewerInstance) {
        if (onDocumentLoaded) {
          viewerInstance.removeEventListener('documentLoaded', onDocumentLoaded);
        }
        if (onDocumentLoadFailed) {
          viewerInstance.removeEventListener('documentLoadFailed', onDocumentLoadFailed);
        }
        if (onZoomUpdated) {
          viewerInstance.removeEventListener('zoomUpdated', onZoomUpdated);
        }

        if (typeof viewerInstance.dispose === 'function') {
          viewerInstance.dispose();
        }
      }

      if (clickableZonesRootRef.current) {
        const rootToUnmount = clickableZonesRootRef.current;
        clickableZonesRootRef.current = null;
        setTimeout(() => rootToUnmount.unmount(), 0);
      }
    };
  }, []);

  useEffect(
    () => {
      if (!documentViewer) return;
      const annotationManager = documentViewer.getAnnotationManager();
      const AllAnnotations = annotationManager.getAnnotationsList();

      if (seeAnnotations) annotationManager.showAnnotations(AllAnnotations);
      else annotationManager.hideAnnotations(AllAnnotations);
    },
    [seeAnnotations, documentViewer],
  );

  useEffect(
    () => {
      if (shouldAddClickableZones && dimensions.height) {
        drawSvgClickableZones();
      }
    },
    [dimensions, shouldAddClickableZones],
  );

  useEffect(
    () => {
      if (!documentViewer || !docUrl) return;
      if (loadedUrlRef.current === docUrl) return;

      (async () => {
        try {
          if (changeLoader) {
            changeLoader(true);
          }

          loadedUrlRef.current = docUrl;
          await loadDocumentBlobFromUrl({
            url: docUrl,
            loadDocument: (blob, options) => documentViewer.loadDocument(blob, options),
            extraOptions: { licenseKey: config.pdfTronLicenseKey },
          });
        } catch (err) {
          loadedUrlRef.current = null;
          reportPdftronException(err, 'LITE_DOCUMENT_FETCH_OR_LOAD_FAILED', {
            tron_id: tronId,
            doc_url: docUrl,
          });
          if (changeLoader) {
            changeLoader(false);
          }
          if (onPageLoadError) {
            onPageLoadError(err);
          }
        }
      })();
    },
    [docUrl, documentViewer],
  );

  return (
    <>
      <div
        key={`scroll-view-${tronId}`}
        id={`scroll-view-${tronId}`}
        className={overflow ? classes.scrollView : classes.scrollViewNoScroll}
      >
        <div className={overflow ? classes.viewer : classes.viewerNoScroll} id={`viewer-${tronId}`}>
          <div id="display-pins">
            {showPins && pins?.map(pin => renderPin({ pin, zoomValue, entityType }))}
          </div>
          <div id="clickToPlacePin" style={{ zIndex: 10 }}>
            {Boolean(shouldAddItems) &&
              !shouldAddClickableZones &&
              entityType && (
                <AddItemButton
                  entityType={entityType}
                  moduleId={moduleId}
                  updatePin={updatePin}
                  onClickFromItem={position => {
                    onPlacePin(position);
                  }}
                  showCustomPin={onPlacePin}
                  isPinsVisible={placing => setShowPins(!placing)}
                />
              )}
          </div>
        </div>
        <div id="parent-pins" />
      </div>
      <Box className={classes.actionSection} sx={{ gridTemplateRows: 'auto !important' }}>
        <Box className={classes.zoomContainer}>
          {Boolean(addZoomButton) && (
            <ZoomControls
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              zoomOutIsDisabled={zoomScale >= zoomValue}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

PdfTronViewer.propTypes = {
  changeLoader: PropTypes.func,
  classes: PropTypes.object,
  docUrl: PropTypes.string,
  entityType: PropTypes.string,
  history: PropTypes.object,
  match: PropTypes.object,
  onPageLoadSuccess: PropTypes.func,
  overflow: PropTypes.bool,
  pins: PropTypes.array,
  onPlacePin: PropTypes.func,
  moduleId: PropTypes.string,
  shouldAddClickableZones: PropTypes.bool,
  tableItems: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  zonesData: PropTypes.object,
  addZoomButton: PropTypes.bool,
  mouseWheelZoom: PropTypes.bool,
  onPageLoadError: PropTypes.func,
  seeAnnotations: PropTypes.bool,
  isFirstPageOnly: PropTypes.bool,
  updatePin: PropTypes.func,
  dispatchGetPlan: PropTypes.func,
  renderPin: PropTypes.func,
};

const mapDispatchToProps = {
  dispatchGetPlan: getPlan,
};

const withConnect = connect(
  null,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  withRouter,
  withStyles(combineStyles(styles, mapTheme)),
)(PdfTronViewer);
